import { useContext, useState, useEffect, useMemo } from 'react'
import { enqueueSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'

import { AppContext } from '/providers/app-context'
import { unarchiveTemplate } from '/providers/templates-api'

import PageWrapper from '/components/page-wrapper'
import ActiveTemplateTable from '/components/templates/template-table'
import TemplateListSidebar from '/components/templates/template-list-sidebar'

import ArchiveDialog from '/components/templates/archive-dialog'
import NotifyAllDialog from '/components/templates/notify-all-dialog'
import DeleteDialog from '/components/templates/delete-dialog'
import TemplateDataExportDialog from '/components/templates/template-data-export-dialog'

import HelpText from '/help/template-list'
import CreateNewTemplate from '/components/templates/create-new-template'
import DuplicateTemplateDialog from '/components/templates/duplicate-template-dialog'
import { currentTemplateStorageKey, getCurrentTemplateId, setCurrentTemplateId } from '/utils/current-template'

const TemplateList = () => {
  const { apiSource,processing, setProcessing, apiKey, templates, refreshTemplates, loadingTemplates } = useContext(AppContext)
  const navigate = useNavigate()

  const [activeTemplates,setActiveTemplates] = useState([])
  const [archivedTemplates,setArchivedTemplates] = useState([])
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false)
  const [notifyOpen,setNotifyOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [showActiveTemplates, setShowActiveTemplates] = useState(true)
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState(() => typeof window !== 'undefined'
    ? window.localStorage.getItem(currentTemplateStorageKey)
    : null)
  const [dataTemplateExportOpen, setDataTemplateExportOpen] = useState(false)
  const [duplicateTemplateOpen, setDuplicateTemplateOpen] = useState(false)

  const selectedTemplate = useMemo(
    () => templates.find(template => String(template.template_id) === String(selectedTemplateId)) ?? null,
    [templates, selectedTemplateId]
  )

  useEffect(() => {
    const active = [].concat(templates).filter(t => !t.archived)
    const archived = [].concat(templates).filter(t => t.archived)

    setActiveTemplates(active)
    setArchivedTemplates(archived)
  }, [templates])

  // Restore the current template after the template list has loaded. This is
  // needed when the selection was made on another page (or while this page
  // was still loading).
  useEffect(() => {
    const currentTemplateId = getCurrentTemplateId()
    if (currentTemplateId && templates.some(template => String(template.template_id) === String(currentTemplateId))) {
      setSelectedTemplateId(currentTemplateId)
    }
  }, [templates])

  const confirmArchiveTemplate = () => {
    if (!selectedTemplate || selectedTemplate.archived) return
    setArchiveConfirmOpen(true)
  }
  
  const doUnarchiveTemplate = async () => {
    if (!selectedTemplate?.archived) return
    setProcessing(true)
    enqueueSnackbar({ message: 'un-archiving template', variant: 'light' })
    const result = await unarchiveTemplate({ apiSource, apiKey, templateId: selectedTemplate.template_id })
    if (result?.error || result?.ok === false) {
      enqueueSnackbar({ message: result?.error?.message ?? 'Unable to unarchive template', variant: 'error' })
      setProcessing(false)
      return
    }
    // TODO handle error
    enqueueSnackbar({ message: 'template un-archived', variant: 'success' })
    setProcessing(false)
    setShowActiveTemplates(true)
    await refreshTemplates()
  }

  const notifyAllPasses = () => {
    if (!selectedTemplate || selectedTemplate.archived) return
    setNotifyOpen(true)
  }

  const confirmDeleteTemplate = () => {
    if (!showActiveTemplates && selectedTemplate?.archived) setDeleteOpen(true)
  }

  const changeTemplateView = value => {
    setShowActiveTemplates(value === 'Active')
  }

  const selectTemplate = templateId => {
    setSelectedTemplateId(templateId)
    setCurrentTemplateId(templateId)
  }

  const finishCreateTemplate = templateId => {
    setCreateTemplateOpen(false)
    setShowActiveTemplates(true)
    selectTemplate(templateId)
    navigate(`/templates/${templateId}/edit`, { replace: true })
  }
  
  return (
    <>
      <PageWrapper title="Your Pass Templates" HelpText={HelpText} heading="Templates" selectedTemplate={selectedTemplate} menu={
        <TemplateListSidebar
          selectedTemplate={selectedTemplate}
          showActiveTemplates={showActiveTemplates}
          onNewTemplate={() => setCreateTemplateOpen(true)}
          onDuplicate={() => setDuplicateTemplateOpen(true)}
          onDownloadDataTemplate={() => setDataTemplateExportOpen(true)}
          onArchive={confirmArchiveTemplate}
          onNotify={notifyAllPasses}
          onUnarchive={doUnarchiveTemplate}
          onDelete={confirmDeleteTemplate}
        />
      }>
        <ActiveTemplateTable
          activeTemplates={activeTemplates}
          archivedTemplates={archivedTemplates}
          loadingTemplates={loadingTemplates}
          selectedTemplateId={selectedTemplateId}
          onTemplateClick={selectTemplate}
          onDataSourceChange={changeTemplateView}
          showActiveTemplates={showActiveTemplates}
        />
      </PageWrapper>

      <ArchiveDialog open={archiveConfirmOpen} setOpen={setArchiveConfirmOpen} template={selectedTemplate}/>
      <NotifyAllDialog open={notifyOpen} setOpen={setNotifyOpen} template={selectedTemplate} processing={processing} setProcessing={setProcessing} />
      <DeleteDialog open={deleteOpen} setOpen={setDeleteOpen} template={selectedTemplate} />
      <TemplateDataExportDialog open={dataTemplateExportOpen} setOpen={setDataTemplateExportOpen} template={selectedTemplate} />
      {createTemplateOpen && <CreateNewTemplate open onClose={() => setCreateTemplateOpen(false)} onCreated={finishCreateTemplate} />}
      <DuplicateTemplateDialog open={duplicateTemplateOpen} onClose={() => setDuplicateTemplateOpen(false)} template={selectedTemplate} />
    </>
  )
}

export default TemplateList
